// frontend/src/utils/pinnedTotals.js
// Helpers for building AG Grid pinned-bottom totals rows. SSRM grids pass the
// backend's full-dataset `totals` to buildPinnedTotalsRow; client-side grids
// compute them with sumFields first.

/**
 * Build a pinned-bottom row payload suitable for AG Grid's pinnedBottomRowData prop.
 *
 * The "row" is a single object whose keys must match column field names.
 * Columns whose field is not in `totals` render blank in the pinned row.
 * The column matching `labelField` carries the label text.
 *
 * Returns an array with the single row, or an empty array if totals is
 * empty/missing. Every source of totals answers an empty set with `{}` or
 * none — the backend's grid endpoints and sumFields — so an empty grid pins
 * no row. AG Grid's `pinnedBottomRowData` is typed `any[]` and the
 * runtime iterates it — passing `null` throws `TypeError: e is not iterable`
 * during the next render pass.
 *
 * The returned row also carries an `__isPinnedTotal: true` marker that
 * cell renderers and `getRowClass` callbacks can use to style differently.
 */
export function buildPinnedTotalsRow(totals, labelField, labelText) {
  if (!totals || Object.keys(totals).length === 0) return [];
  return [{ __isPinnedTotal: true, ...totals, [labelField]: labelText }];
}

/**
 * Sum numeric fields across rows. No rows sum to `{}`, not to zeros: an empty
 * grid has nothing to total, so it pins no row.
 */
export function sumFields(rows, fields) {
  if (rows.length === 0) return {};
  return Object.fromEntries(fields.map((f) => [f, rows.reduce((sum, row) => sum + row[f], 0)]));
}

/**
 * True for the pinned-bottom totals row. A total is a derived aggregate of the
 * rows above it, so it must never be editable — typing into it produces a value
 * with no write-back target that vanishes on the next recompute.
 *
 * Every editable column on a grid that renders a totals row MUST gate its
 * `editable` callback through this. The `child_count == null` / `!isGroupRow`
 * "is this a leaf" proxies do NOT exclude the pinned row (it carries no
 * `child_count`). Checks both the AG Grid `node.rowPinned` flag and our own
 * `__isPinnedTotal` marker.
 */
export function isPinnedTotalRow(params) {
  return Boolean(params?.node?.rowPinned || params?.data?.__isPinnedTotal);
}

/**
 * Helper for AG Grid `getRowClass` to identify pinned-total rows so they can
 * receive distinct styling (heavier border, semi-bold font, etc.).
 */
export function pinnedTotalRowClass(params) {
  if (params.node?.rowPinned === 'bottom' || params.data?.__isPinnedTotal) {
    return 'ag-row-pinned-total';
  }
  return undefined;
}

/**
 * Wrap a column's `editable` rule so the pinned totals row is never editable,
 * whatever the column's own logic says. A total is a derived aggregate with no
 * writable record behind it — letting it enter edit mode produces a value that
 * vanishes on the next recompute. Boolean `editable: true` and callback forms
 * are both handled; `false`/undefined columns are left untouched by the caller.
 */
export function pinnedAwareEditable(originalEditable) {
  return (params) => {
    if (isPinnedTotalRow(params)) return false;
    return typeof originalEditable === 'function'
      ? originalEditable(params)
      : Boolean(originalEditable);
  };
}

/**
 * Wrap an existing valueFormatter so it renders empty for pinned-row cells
 * whose field isn't part of the totals payload. Without this, formatters
 * that explicitly fall back to '-' / '—' on null make the pinned
 * row look like it has aggregates everywhere — including columns where no
 * sum makes sense (per-unit prices, lead time, location, MOQ, ...).
 *
 * Applied via {@link wrapColumnsForPinnedTotals} to columns that already
 * carry an inline `valueFormatter`. Safe on grids without pinned rows: the
 * `params.node?.rowPinned` check is falsy for regular rows so formatting
 * passes through unchanged.
 */
export function pinnedAwareFormatter(originalFormatter) {
  return (params) => {
    if (params.node?.rowPinned && (params.value === undefined || params.value === null)) {
      return '';
    }
    return originalFormatter(params);
  };
}

/**
 * Walk a column-defs tree and make every leaf column pinned-total aware. Use
 * this on grids that render a pinned-bottom totals row. Two things happen per
 * leaf column:
 *
 *  1. `editable` is wrapped so the totals row can never enter edit mode (see
 *     {@link pinnedAwareEditable}), whatever a new column declares.
 *  2. `valueFormatter` (when present inline) is wrapped so columns without an
 *     aggregate value render empty instead of a misleading "0" / "—" / "-".
 *
 * Columns that rely on a `type:` from {@link useColumnTypes} for formatting
 * (no inline `valueFormatter`) are intentionally skipped for (2) — installing
 * an inline formatter here would override the type's formatter (AG Grid
 * resolves inline `valueFormatter` before the type-level one). Each type in
 * `useColumnTypes` owns its pinned-row null behavior directly via a
 * `params.node?.rowPinned && params.value == null` guard.
 *
 * Custom `cellRenderer` columns are still expected to handle pinned rows on
 * their own (most read `params.value` and naturally render empty for null;
 * those that read row metadata — e.g. `OverrideCellRenderer` — guard via
 * `params.node?.rowPinned`).
 */
export function wrapColumnsForPinnedTotals(columnDefs) {
  if (!Array.isArray(columnDefs)) return columnDefs;
  return columnDefs.map((col) => {
    if (!col) return col;
    if (col.children) {
      return { ...col, children: wrapColumnsForPinnedTotals(col.children) };
    }
    let wrapped = col;
    if (col.editable) {
      wrapped = { ...wrapped, editable: pinnedAwareEditable(col.editable) };
    }
    if (typeof col.valueFormatter === 'function') {
      wrapped = { ...wrapped, valueFormatter: pinnedAwareFormatter(col.valueFormatter) };
    }
    return wrapped;
  });
}
